import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, loginUser } from "../api/auth";
import { useAuth } from "../hooks/useAuth";

export default function Register() {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
        setServerError("");
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim())
            newErrors.firstName = "First name is required.";
        if (!formData.lastName.trim())
            newErrors.lastName = "Last name is required.";
        if (!formData.email.trim()) newErrors.email = "Email is required.";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Enter a valid email.";
        if (!formData.password) newErrors.password = "Password is required.";
        else if (formData.password.length < 8)
            newErrors.password = "Password must be at least 8 characters.";
        if (formData.password !== formData.confirmPassword)
            newErrors.confirmPassword = "Passwords do not match.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError("");
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            // Register the user
            await registerUser(formData);

            // Auto login after registration
            const data = await loginUser(formData.email, formData.password);
            login(
                {
                    email: data.user_email,
                    name: data.user_display_name,
                    nicename: data.user_nicename,
                },
                data.token,
            );
            navigate("/my-account", { replace: true });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                "Registration failed. Please try again.";
            setServerError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-md">
                {/* Brand */}
                <div className="text-center mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2.5"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-xl flex items-center justify-center shadow-md shadow-secondary-500/20">
                            <svg
                                viewBox="0 0 24 24"
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 2L20 12L12 22L4 12Z" />
                                <path d="M8 12H16" />
                                <path d="M12 8V16" />
                            </svg>
                        </div>
                        <span className="text-2xl font-extrabold tracking-tight">
                            <span className="text-neutral-900">Fits</span>
                            <span className="text-secondary-600">Aura</span>
                        </span>
                    </Link>
                    <h1 className="text-xl font-bold text-neutral-900 mt-6">
                        Create an account
                    </h1>
                    <p className="text-neutral-500 text-sm mt-1">
                        Join us and start shopping
                    </p>
                </div>

                <div className="card p-8">
                    {serverError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
                            {serverError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                        {/* Name row */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                                <label className="label" htmlFor="firstName">
                                    First Name
                                </label>
                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    className="input"
                                    placeholder="John"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                                {errors.firstName && (
                                    <p className="error-text">
                                        {errors.firstName}
                                    </p>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="label" htmlFor="lastName">
                                    Last Name
                                </label>
                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    className="input"
                                    placeholder="Doe"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                />
                                {errors.lastName && (
                                    <p className="error-text">
                                        {errors.lastName}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="label" htmlFor="email">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                className="input"
                                placeholder="john@example.com"
                                value={formData.email}
                                onChange={handleChange}
                            />
                            {errors.email && (
                                <p className="error-text">{errors.email}</p>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="label" htmlFor="password">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                className="input"
                                placeholder="Min. 8 characters"
                                value={formData.password}
                                onChange={handleChange}
                            />
                            {errors.password && (
                                <p className="error-text">{errors.password}</p>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="label" htmlFor="confirmPassword">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                className="input"
                                placeholder="Re-enter password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                            />
                            {errors.confirmPassword && (
                                <p className="error-text">
                                    {errors.confirmPassword}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn-primary w-full mt-2"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                "Create Account"
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center text-sm text-neutral-500 mt-6">
                    Already have an account?{" "}
                    <Link
                        to="/login"
                        className="text-primary-600 font-semibold hover:underline"
                    >
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
